
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { User, LogOut, Settings, UserCircle } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    signOut
  } = useAuth();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const navLinks = [
    { name: 'Home', path: '/' },
    ...(!user ? [{ name: 'Features + Pricing', path: '/pricing' }] : []),
  ];
  const handleLogoClick = (e: React.MouseEvent) => {
    // If we're already on the home page, scroll to top instead of navigating
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const handleSignOut = async () => {
    await signOut();
  };
  return <motion.header className={`fixed top-0 left-0 right-0 z-50 px-6 md:px-10 lg:px-20 py-4 transition-all duration-300 ${scrolled ? 'glass shadow-sm' : 'bg-transparent'}`} initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.5,
    ease: [0.22, 1, 0.36, 1]
  }}>
      <nav className="flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="font-semibold text-xl" onClick={handleLogoClick}>
          FantasyWiz.ai
        </Link>
        
        <div className="hidden md:flex items-center space-x-8">
          {navLinks.map(link => <Link key={link.name} to={link.path} className={`text-sm font-medium transition-colors hover:text-primary relative ${location.pathname === link.path ? 'text-primary' : 'text-foreground/80 hover:text-foreground'}`}>
              {link.name}
              {location.pathname === link.path && <motion.div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" layoutId="navIndicator" />}
            </Link>)}
          
          {user && <>
              
              
              <Link to="/account" className={`text-sm font-medium transition-colors hover:text-primary relative ${location.pathname === '/account' ? 'text-primary' : 'text-foreground/80 hover:text-foreground'}`}>
                Account
                {location.pathname === '/account' && <motion.div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" layoutId="navIndicator" />}
              </Link>
            </>}
        </div>
        
        {user ? <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2 rounded-full">
                <span className="md:hidden">Account</span>
                <Avatar className="h-8 w-8">
                  <AvatarFallback>
                    <UserCircle className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/account')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Account Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu> : <div className="flex items-center space-x-4">
            {location.pathname !== '/signup' && location.pathname !== '/login' && <>
                {location.pathname !== '/signup' && <Button asChild className="h-8 text-xs px-3">
                    <Link to="/signup">Sign Up</Link>
                  </Button>}
                {location.pathname !== '/login' && <Button asChild variant="outline" className="h-8 text-xs px-3">
                    <Link to="/login">Log In</Link>
                  </Button>}
              </>}
          </div>}
      </nav>
    </motion.header>;
};
export default Navbar;
